import React, { useEffect, useState } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow, Polyline, MarkerF, MarkerClustererF } from '@react-google-maps/api';
import { GET_MAP_ACTIVE_LOADS, GET_DRIVER_DISTANCE, GET_DRIVER_LOCATIONS } from '../../../graphql/client/queries';
import { Query } from '../../_common';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useLazyQuery } from '@apollo/client/react';
import { Error, Loading } from '../../_common';
import {
    GetMapActiveData_getMapActiveData_maps as mapData,
    GetMapActiveData_getMapActiveData_onshiftdrivers as onShiftDriver,
    GetMapActiveData,
    GetDriverDistance,
    GetDriverLocations,
} from '../../../types/schemaTypes';
import { DashboardFilters } from '../../Filter';
import DashboardHeader from '../../Header/DashboardHeader';
import { useStoreContext } from '../../App/StoreProvider';
import { formatDateUtcToLocal } from '../../../utils/helpers/datetimeHelper';
import lofaIcon from '../../../assets/lofaIcon.png';
import oilWellIcon from '../../../assets/oilWell.png';
import redTruckIcon from '../../../assets/redTruck.png';
import whiteTruckIcon from '../../../assets/whiteTruck.png';
import blueTruckIcon from '../../../assets/blueTruck.png';
import tealTruckIcon from '../../../assets/tealTruck.png';
import orangeTruckIcon from '../../../assets/orangeTruck.png';
import greenTruckIcon from '../../../assets/greenTruck.png';

import * as LoadManagementListActions from '../../../store/actions/loadManagementList';

const UNAVAILABLE = 'Not available';

interface DriverMap {
    driverLat: null | number | string | undefined | any;
    driverLong: null | number | string | undefined | any;
    driverName: null | number | string | undefined | any;
    driverId: null | number | string | undefined | any;
    driverLastUpdated: null | number | string | undefined | any;
    companyName: null | number | string | undefined | any;
    carrierId: null | number | string | undefined | any;
    loadId: null | number | string | undefined | any;
    loadStatus: null | number | string | undefined | any;
    wellName: null | number | string | undefined | any;
    wellLat: null | number | string | undefined | any;
    wellLong: null | number | string | undefined | any;
    jobId: null | number | string | undefined | any;
    proppantName: null | number | string | undefined | any;
    milesFromWell: null | number | string | undefined | any;
    regionId: null | number | string | undefined | any;
    regionName: null | number | string | undefined | any;
    lofaName: null | number | string | undefined | any;
    lofaLat: null | number | string | undefined | any;
    lofaLong: null | number | string | undefined | any;
    lofaId: null | number | string | undefined | any;
    averageLoadTime: null | number | string | undefined | any;
    proppantId: null | number | string | undefined | any;
    numberTrucksWaitingAtWell: null | number | string | undefined | any;
    numberofTrucksEnrouteToWell: null | number | string | undefined | any;
    numberTrucksWaitingAtLofa: null | number | string | undefined | any;
    numberofTrucksEnrouteAtLofa: null | number | string | undefined | any;
    averageUnloadTime: null | number | string | undefined | any;
    cellPhone: null | number | string | undefined | any;
    customerId: null | number | string | undefined | any;
    atLofaIn: null | number | string | undefined | any;
    atLofaOut: null | number | string | undefined | any;
    avgDriveTimeLofaToWell: null | number | string | undefined | any;
    driveTimeToWellSoFar: null | number | string | undefined | any;
}

interface NoLoadDriver {
    driverId: null | number | string | undefined | any;
    driverLat: null | number | string | undefined | any;
    driverLong: null | number | string | undefined | any;
    driverName: null | number | string | undefined | any;
    companyName: null | number | string | undefined | any;
    cellPhone: null | number | string | undefined | any;
    carrierId: null | number | string | undefined | any;
    driverLastUpdated: null | number | string | undefined | any;
}

const containerStyle = { width: '78vw', height: '90vh' };
const center = { lat: 29.9120212978801, lng: -95.7501210858066 };

function mapIcon(driver: DriverMap) {
    switch (driver.loadStatus) {
        case 'Assigned':
            return blueTruckIcon;
        case 'On-site':
            return greenTruckIcon;
        case 'Loading':
            return tealTruckIcon;
        case 'Loaded':
            if (driver.driveTimeToWellSoFar > driver.avgDriveTimeLofaToWell) return redTruckIcon;
            else return orangeTruckIcon;
    }
}

const DashboardMap = (): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const abortController = new AbortController();
    const [listHeight, setListHeight] = useState(window.screen.height);
    const abortRequest = (): void => {
        abortController.abort();
    };

    useEffect(() => {
        dispatch(LoadManagementListActions.resetFilterState());
    }, []);

    const setListContainerHeight = (): void => {
        //begin code for filters
        const filterDiv = document.getElementById('loadsFilterContainer');
        const listDiv = document.getElementById('loadsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };

    const {
        loadManagementList: { filters, searchPhrase },
    } = state;

    const variables = React.useMemo(()=>{
        
        return{
        input: {
            lofas: filters.lofaIds,
            jobIds: filters.jobIds,
            carriers: filters.carrierIds,
            regionIds: filters.regionIds,
            proppants: filters.proppantIds,
            drivers: filters.driverIds,
            customerIds: filters.customerIds,
            statuses: filters.statuses,
            search: searchPhrase,
        },
    }},[filters.lofaIds,filters.jobIds,filters.carrierIds,filters.regionIds,filters.proppantIds,filters.driverIds,filters.customerIds,filters.statuses,searchPhrase]);

    const initialState = {
        driverLat: null,
        driverLong: null,
        driverName: null,
        driverId: null,
        driverLastUpdated: null,
        companyName: null,
        carrierId: null,
        loadId: null,
        loadStatus: null,
        wellName: null,
        wellLat: null,
        wellLong: null,
        jobId: null,
        proppantName: null,
        milesFromWell: null,
        regionId: null,
        regionName: null,
        lofaName: null,
        lofaLat: null,
        lofaLong: null,
        lofaId: null,
        averageLoadTime: 0,
        proppantId: null,
        numberTrucksWaitingAtWell: null,
        numberofTrucksEnrouteToWell: null,
        numberTrucksWaitingAtLofa: null,
        numberofTrucksEnrouteAtLofa: null,
        averageUnloadTime: 0,
        cellPhone: null,
        customerId: null,
        atLofaIn: null,
        atLofaOut: null,
        avgDriveTimeLofaToWell: 0,
    };

    const initialNoLoadDriver = {
        driverId: null,
        driverLat: null,
        driverLong: null,
        driverName: null,
        companyName: null,
        cellPhone: null,
        carrierId: null,
        driverLastUpdated: null,
    };

    interface DriverPosition {
        lat: number;
        lng: number;
    }

    const initialDriverPath: DriverPosition[] = [];
    const [selectDriverPath, setSelectedDriverPath] = useState(initialDriverPath);
    const [distanceRemain, setDistanceRemain] = useState(0);
    const [timeRemain, setTimeRemain] = useState(0);
    const [selectedLofa, setSelectedLofa] = useState(initialState);
    const [selectedWellsite, setSelectedWellsite] = useState(initialState);
    const [selectedNoLoadDriver, setSelectedNoLoadDriver] = useState(initialNoLoadDriver);
    const [selectedDriver, setSelectedDriver] = useState(initialState);
    const [googleDriverDistance, { loading: getDriverLoading, data: driverDistanceData }] = useLazyQuery<GetDriverDistance>(
        GET_DRIVER_DISTANCE,
        { fetchPolicy: 'no-cache' },
    );

    const [driverPath, { loading: getDriverPathLoading, data: driverPathData }] = useLazyQuery<GetDriverLocations>(GET_DRIVER_LOCATIONS, {
        fetchPolicy: 'no-cache',
    });

    const mapRef = React.useRef<google.maps.Map | null>(null);
    const onMapLoad = React.useCallback(map => {
        mapRef.current = map;
    }, []);

    const timeConverter = (minutes: string | null): string => {
        if (minutes != null) {
            const hours = parseInt(minutes) / 60;
            const rhours = Math.floor(hours);
            const min = (hours - rhours) * 60;
            const rmin = Math.round(min);
            return rhours + 'h' + ' ' + rmin + 'm';
        } else {
            return '';
        }
    };

    const timeAdd = (minutes: any | null): string => {
        if (minutes != null) {
            const currentDate = new Date();
            const futureDate = new Date(currentDate.getTime() + minutes * 60000);
            return futureDate.toLocaleTimeString();
        } else {
            return '';
        }
    };

    const onCloseInfoWindow = (): void => {
        setSelectedDriver(initialState);
        setSelectedWellsite(initialState);
        setSelectedLofa(initialState);
        setSelectedNoLoadDriver(initialNoLoadDriver);
        setDistanceRemain(0);
        setTimeRemain(0);
        setSelectedDriverPath(initialDriverPath);
    };
    //begin code for map
    const onMapClick = React.useCallback(e => {
        onCloseInfoWindow();
    }, []);

    useEffect(() => {
        if (driverDistanceData?.getDriverDistance && !getDriverLoading) {
            const d = driverDistanceData.getDriverDistance;
            if ((d.distanceToLofa ?? 0) > 0) setDistanceRemain(d.distanceToLofa ?? 0);
            else setDistanceRemain(d.distanceToWellsite ?? 0);
            setTimeRemain(d.timeToDestinationInMinutes ?? 0);
        }
    }, [driverDistanceData, getDriverLoading]);

    useEffect(() => {
        if (driverPathData && driverPathData.getDriverLocations && !getDriverPathLoading) {
            if (driverPathData && driverPathData.getDriverLocations) {
                const tmpPath: DriverPosition[] = [];
                driverPathData.getDriverLocations.forEach((item: any | null): void => {
                    if (item !== null) {
                        tmpPath.push({
                            lat: item.latitude,
                            lng: item.longitude,
                        });
                    }
                });
                setSelectedDriverPath(tmpPath);
            }
        }
    }, [driverPathData, getDriverPathLoading]);

    const lineSymbol = {
        path: 'M 1.5 1 L 1 0 L 1 2 M 0.5 1 L 1 0',
        fillColor: 'black',
        strokeColor: 'black',
        strokeWeight: 2,
        strokeOpacity: 1,
    };

    return (
        <Query<GetMapActiveData> query={GET_MAP_ACTIVE_LOADS}
        variables={{jobId: 0}} 
        pollInterval={300000}>
            {({ data, loading, error }): React.ReactElement | null => {
                let currentWellSites: DriverMap[] = [];
                let currentLofas: DriverMap[] = [];
                let currentDrivers: DriverMap[] = [];

                if (loading) {
                    return (
                        <div className="Filter-Loading">
                            <Loading />
                        </div>
                    );
                }
                if (error) {
                    return <Error error={error} />;
                }

                if (data && data.getMapActiveData && data.getMapActiveData.maps) {
                    const distinctWellsite = new Map();
                    const distinctLofa = new Map();

                    const activeLoadList: mapData[] = [];
                    if (data.getMapActiveData.maps !== null && data.getMapActiveData.maps.length !== 0) {
                        data.getMapActiveData.maps.forEach((item) => {
                            if (item !== null) activeLoadList.push(item as mapData);
                        });
                    }

                    const distinctDriver = new Map();
                    activeLoadList.forEach((element: any) => {
                        if (
                            element.driverId !== null &&
                            element.driverLat !== null &&
                            !distinctDriver.has(element.driverId)
                        ) {
                            distinctDriver.set(element.driverId, true);
                            const tempDriver = {...element};
                            
                            tempDriver.distanceToLofa= 0;
                           // Object.preventExtensions(tempDriver);
                            tempDriver.distanceToWellsite = 0;
                            tempDriver.timeToDestinationInMinutes = 0;
                            
                            currentDrivers.push(tempDriver);
                        }

                        //get distinct well site list
                        if (!distinctWellsite.has(element.jobId)) {
                            distinctWellsite.set(element.jobId, true);
                            const tempWell= {...element};
                            tempWell.numberTrucksWaitingAtWell = activeLoadList.filter(
                                t => t.loadStatus === 'On-site' && t.jobId === element.jobId,
                            ).length;
                            tempWell.numberofTrucksEnrouteToWell = activeLoadList.filter(
                                t => t.loadStatus === 'Loaded' && t.jobId === element.jobId,
                            ).length;
                            currentWellSites.push(tempWell);
                        }

                        //get distinct Lofa list
                        if (!distinctLofa.has(element.lofaId)) {
                            distinctLofa.set(element.lofaId, true);
                            const tempLofa = {...element};
                            tempLofa.numberTrucksWaitingAtLofa = activeLoadList.filter(
                                t => t.loadStatus === 'Loading' && t.lofaId === element.lofaId,
                            ).length;
                            tempLofa.numberofTrucsEnrouteToLofa = activeLoadList.filter(
                                t => t.loadStatus === 'Assigned' && t.lofaId === element.lofaId,
                            ).length;
                            currentLofas.push(tempLofa);
                        }
                    });
                }

                let noLoadDriverList: NoLoadDriver[] = [];
                if (data && data.getMapActiveData && data.getMapActiveData.onshiftdrivers) {
                    if (
                        data.getMapActiveData.onshiftdrivers !== null &&
                        data.getMapActiveData.onshiftdrivers.length !== 0
                    ) {
                        data.getMapActiveData.onshiftdrivers.forEach((item) => {
                            if (
                                item &&
                                item.driverId !== null &&
                                item.driverLat !== null &&
                                item.driverLong !== null &&
                                item.carrierId != null
                            ) {
                                noLoadDriverList.push({
                                    driverId: item.driverId,
                                    driverLat: item.driverLat,
                                    driverLong: item.driverLong,
                                    driverName: item.driverName,
                                    companyName: item.companyName,
                                    cellPhone: item.cellPhone,
                                    carrierId: item.carrierId,
                                    driverLastUpdated: item.driverLastUpdated,
                                });
                            }
                        });
                    }
                }

                if (variables.input.statuses.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.statuses.includes(l.loadStatus));
                    currentLofas = currentLofas.filter(l => variables.input.statuses.includes(l.loadStatus));
                    currentDrivers = currentDrivers.filter(l => variables.input.statuses.includes(l.loadStatus));
                }

                if (variables.input.jobIds.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.jobIds.includes(l.jobId));
                    currentLofas = currentLofas.filter(l => variables.input.jobIds.includes(l.jobId));
                    currentDrivers = currentDrivers.filter(l => variables.input.jobIds.includes(l.jobId));
                }

                if (variables.input.customerIds.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.customerIds.includes(l.customerId));
                    currentLofas = currentLofas.filter(l => variables.input.customerIds.includes(l.customerId));
                    currentDrivers = currentDrivers.filter(l => variables.input.customerIds.includes(l.customerId));
                }

                if (variables.input.lofas.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.lofas.includes(l.lofaId));
                    currentLofas = currentLofas.filter(l => variables.input.lofas.includes(l.lofaId));
                    currentDrivers = currentDrivers.filter(l => variables.input.lofas.includes(l.lofaId));
                }

                if (variables.input.carriers.length > 0) {
                    currentDrivers = currentDrivers.filter(l => variables.input.carriers.includes(l.carrierId));
                    noLoadDriverList = noLoadDriverList.filter(l => variables.input.carriers.includes(l.carrierId));
                }

                if (variables.input.regionIds.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.regionIds.includes(l.regionId));
                    currentLofas = currentLofas.filter(l => variables.input.regionIds.includes(l.regionId));
                    currentDrivers = currentDrivers.filter(l => variables.input.regionIds.includes(l.regionId));
                }

                if (variables.input.proppants.length > 0) {
                    currentWellSites = currentWellSites.filter(l => variables.input.proppants.includes(l.proppantId));
                    currentLofas = currentLofas.filter(l => variables.input.proppants.includes(l.proppantId));
                    currentDrivers = currentDrivers.filter(l => variables.input.proppants.includes(l.proppantId));
                }

                if (variables.input.drivers.length > 0) {
                    currentDrivers = currentDrivers.filter(l => variables.input.drivers.includes(l.driverId));
                }
               
                return (
                    <div style={{width:'100vw'}}>
                        <div  className="Dashboard-Container">
                            <DashboardHeader />
                            <div  style={{display:'flex'}}className="Dashboard-Body">
                                <div id="loadsFilterContainer" className="Dashboard-Filter">
                                    <DashboardFilters
                                        abortRequest={abortRequest}
                                        onFilterToggle={setListContainerHeight}
                                    />
                                </div>

                                <div id="loadsListContainer" className="Dashboard-Map" style={{ height: listHeight }}>
                                    <LoadScript googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
                                    {/* <LoadScript googleMapsApiKey=""> */}
                                        <GoogleMap
                                            mapContainerStyle={containerStyle}
                                            center={center}
                                            zoom={5}
                                            onClick={onMapClick}
                                            onLoad={onMapLoad}
                                        >
                                            {/* WellSite Marker */}
                                            {currentWellSites.length >0 && <MarkerClustererF>
                                                {(clusterer) => 
                                                <>
                                                     {currentWellSites?.map((wellSite: DriverMap) =>  { 
                                                        return (
                                                            <>
                                                        <MarkerF
                                                            key={wellSite.jobId}
                                                            position={{
                                                                lat: wellSite.wellLat,
                                                                lng: wellSite.wellLong,
                                                            }}
                                                            clusterer={clusterer}
                                                            icon={oilWellIcon}
                                                            onClick={() => {
                                                                setSelectedWellsite(wellSite);
                                                            }}
                                                        ></MarkerF></>
                                                    ) })
                                                     }
                                                     </>
                                                     }
                                            </MarkerClustererF>}

                                            {/* Lofa Marker */}

                                            {currentLofas.length > 0 && <MarkerClustererF>
                                                {(clusterer) => (
                                                 <>
                                                    {currentLofas.map((lofa: DriverMap) => (
                                                        <MarkerF
                                                            key={lofa.lofaId}
                                                            position={{ lat: lofa.lofaLat, lng: lofa.lofaLong }}
                                                            clusterer={clusterer}
                                                            icon={lofaIcon}
                                                            onClick={() => {
                                                                setSelectedLofa(lofa);
                                                            }}
                                                        ></MarkerF>
                                                    ))
                                                         } </>
                                                )}
                                            </MarkerClustererF>}

                                            {/* Driver on Load Marker */}
                                            {currentDrivers.length >0 && <MarkerClustererF>
                                                {(clusterer) => (
                                                    <>
                                                    {currentDrivers.map(driver => (
                                                        <MarkerF
                                                            key={driver.driverId}
                                                            position={{
                                                                lat: driver.driverLat,
                                                                lng: driver.driverLong,
                                                            }}
                                                            clusterer={clusterer}
                                                            icon={mapIcon(driver)}
                                                            onClick={() => {
                                                                setSelectedDriver(driver);

                                                                driverPath({
                                                                    variables: {
                                                                        input: {
                                                                            loadId: driver.loadId,
                                                                        },
                                                                    },
                                                                });

                                                                if (
                                                                    driver.loadStatus === 'Loaded' ||
                                                                    driver.loadStatus === 'Assigned'
                                                                ) {
                                                                    const dest =
                                                                        driver.loadStatus === 'Loaded'
                                                                            ? 'Wellsite'
                                                                            : 'LOFA';
                                                                    googleDriverDistance({
                                                                        variables: {
                                                                            input: {
                                                                                driver: {
                                                                                    driverId: driver.driverId,
                                                                                    driverLat: driver.driverLat,
                                                                                    driverLong: driver.driverLong,
                                                                                    wellLat: driver.wellLat,
                                                                                    wellLong: driver.wellLong,
                                                                                    lofaLat: driver.lofaLat,
                                                                                    lofaLong: driver.lofaLong,
                                                                                    distanceToLofa: 0,
                                                                                    distanceToWellsite: 0,
                                                                                    timeToDestinationInSeconds: 0,
                                                                                    timeToDestinationInMinutes: 0,
                                                                                },
                                                                                destination: dest,
                                                                            },
                                                                        },
                                                                    });
                                                                }
                                                            }}
                                                        ></MarkerF>
                                                    ))
                                                        }</>
                                                )}
                                            </MarkerClustererF>}

                                            {/* Driver with NO load Marker */}

                                            {noLoadDriverList.length >0 &&<MarkerClustererF>
                                                {(clusterer) => (
                                                    <>
                                                    {
                                                    variables.input.statuses.length > 0 ||
                                                    variables.input.jobIds.length > 0 ||
                                                    variables.input.lofas.length > 0 ||
                                                    variables.input.regionIds.length > 0 ||
                                                    variables.input.proppants.length > 0 ||
                                                    variables.input.drivers.length > 0
                                                        ? null
                                                        : noLoadDriverList.map(noLoadDriver => (
                                                              <MarkerF
                                                                  key={noLoadDriver.driverId}
                                                                  position={{
                                                                      lat: noLoadDriver.driverLat,
                                                                      lng: noLoadDriver.driverLong,
                                                                  }}
                                                                  clusterer={clusterer}
                                                                  icon={whiteTruckIcon}
                                                                  onClick={() => {
                                                                      setSelectedNoLoadDriver(noLoadDriver);
                                                                  }}
                                                              ></MarkerF>
                                                          ))
                                                                }</>    
                                               )}
                                            </MarkerClustererF>}

                                            {selectedDriver.driverId !== null ? (
                                                <InfoWindow
                                                    position={{
                                                        lat: selectedDriver.driverLat || 0,
                                                        lng: selectedDriver.driverLong || 0,
                                                    }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                                    onCloseClick={() => {
                                                        setSelectedDriver(initialState);
                                                        setDistanceRemain(0);
                                                        setTimeRemain(0);
                                                    }}
                                                >
                                                    <div>
                                                        <Col>
                                                            <Row>
                                                                <span className="TextContent-Title">Load ID:</span>
                                                                <span className="TextContent-Title">
                                                                    {selectedDriver.loadId}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Load Status:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.loadStatus}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Well Site:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.wellName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">LOFA:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.lofaName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Proppant:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.proppantName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Driver:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.driverName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Driver Phone:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.cellPhone}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Carrier Name:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.companyName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Location updated:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedDriver.driverLastUpdated
                                                                        ? formatDateUtcToLocal(
                                                                              selectedDriver.driverLastUpdated,
                                                                          )
                                                                        : UNAVAILABLE}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                {selectedDriver.loadStatus === 'Assigned' && (
                                                                    <Col>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Distance to LOFA:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {distanceRemain} miles
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Time remain to LOFA:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {timeConverter(timeRemain.toString())}{' '}
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                ETA to Well Site:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {timeAdd(
                                                                                    selectedDriver.averageLoadTime
                                                                                        ? selectedDriver.averageLoadTime +
                                                                                              timeRemain +
                                                                                              selectedDriver.avgDriveTimeLofaToWell
                                                                                        : timeRemain +
                                                                                              selectedDriver.avgDriveTimeLofaToWell,
                                                                                )}
                                                                            </span>
                                                                        </Row>
                                                                    </Col>
                                                                )}

                                                                {selectedDriver.loadStatus === 'Loading' && (
                                                                    <Col>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Miles to Well Site:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {selectedDriver.milesFromWell} miles
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                ETA to Well Site:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {timeAdd(
                                                                                    selectedDriver.avgDriveTimeLofaToWell +
                                                                                        selectedDriver.averageLoadTime,
                                                                                )}
                                                                            </span>
                                                                        </Row>
                                                                    </Col>
                                                                )}

                                                                {selectedDriver.loadStatus === 'Loaded' && (
                                                                    <Col>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Departed LOFA:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {selectedDriver.atLofaOut
                                                                                    ? formatDateUtcToLocal(
                                                                                          selectedDriver.atLofaOut,
                                                                                      )
                                                                                    : UNAVAILABLE}
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Distance to Wellsite:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {distanceRemain} miles{' '}
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                Time remain to Wellsite:
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {timeConverter(timeRemain.toString())}{' '}
                                                                            </span>
                                                                        </Row>
                                                                        <Row>
                                                                            <span className="TextContent-Title">
                                                                                ETA to Well Site:{' '}
                                                                            </span>
                                                                            <span className="TextContent-ValueDarkGreen">
                                                                                {timeAdd(timeRemain)}
                                                                            </span>
                                                                        </Row>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        </Col>
                                                    </div>
                                                </InfoWindow>
                                            ) : null}

                                            {selectedLofa.lofaId !== null ? (
                                                <InfoWindow
                                                    position={{
                                                        lat: selectedLofa.lofaLat || 0,
                                                        lng: selectedLofa.lofaLong || 0,
                                                    }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                                    onCloseClick={() => {
                                                        setSelectedLofa(initialState);
                                                    }}
                                                >
                                                    <div>
                                                        <Col>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Loading Facility:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedLofa.lofaName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Average Load Time:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedLofa.averageLoadTime
                                                                        ? selectedLofa.averageLoadTime + ' minutes'
                                                                        : UNAVAILABLE}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    # Trucks waiting:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedLofa.numberTrucksWaitingAtLofa
                                                                        ? selectedLofa.numberTrucksWaitingAtLofa
                                                                        : 0}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    # Trucks enroute:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedLofa.numberofTrucksEnrouteAtLofa || 0
                                                                        ? selectedLofa.numberofTrucksEnrouteAtLofa || 0
                                                                        : 0}
                                                                </span>
                                                            </Row>
                                                        </Col>
                                                    </div>
                                                </InfoWindow>
                                            ) : null}

                                            {selectedNoLoadDriver.driverId !== null ? (
                                                <InfoWindow
                                                    position={{
                                                        lat:
                                                            selectedNoLoadDriver.driverLat || 0
                                                                ? selectedNoLoadDriver.driverLat || 0
                                                                : 0,
                                                        lng:
                                                            selectedNoLoadDriver.driverLong || 0
                                                                ? selectedNoLoadDriver.driverLong || 0
                                                                : 0,
                                                    }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                                    onCloseClick={() => {
                                                        setSelectedNoLoadDriver(initialNoLoadDriver);
                                                    }}
                                                >
                                                    <div>
                                                        <Col>
                                                            <Row>
                                                                <span className="TextContent-Title">Driver Name:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedNoLoadDriver.driverName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Driver Phone:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedNoLoadDriver.cellPhone}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">Carrier Name:</span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedNoLoadDriver.companyName
                                                                        ? selectedNoLoadDriver.companyName
                                                                        : UNAVAILABLE}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Location updated:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedNoLoadDriver.driverLastUpdated
                                                                        ? formatDateUtcToLocal(
                                                                              selectedNoLoadDriver.driverLastUpdated,
                                                                          )
                                                                        : UNAVAILABLE}
                                                                </span>
                                                            </Row>
                                                        </Col>
                                                    </div>
                                                </InfoWindow>
                                            ) : null}

                                            {selectedWellsite.loadId !== null ? (
                                                <InfoWindow
                                                    position={{
                                                        lat: selectedWellsite.wellLat || 0,
                                                        lng: selectedWellsite.wellLong || 0,
                                                    }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                                    onCloseClick={() => {
                                                        setSelectedWellsite(initialState);
                                                    }}
                                                >
                                                    <div>
                                                        <Col>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Wellsite Name:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {' '}
                                                                    {selectedWellsite.wellName}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    Average Unload Time:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedWellsite.averageUnloadTime
                                                                        ? selectedWellsite.averageUnloadTime +
                                                                          ' minutes'
                                                                        : UNAVAILABLE}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    # Trucks waiting:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedWellsite.numberTrucksWaitingAtWell
                                                                        ? selectedWellsite.numberTrucksWaitingAtWell
                                                                        : 0}
                                                                </span>
                                                            </Row>
                                                            <Row>
                                                                <span className="TextContent-Title">
                                                                    # Trucks enroute:
                                                                </span>
                                                                <span className="TextContent-ValueDarkGreen">
                                                                    {selectedWellsite.numberofTrucksEnrouteToWell
                                                                        ? selectedWellsite.numberofTrucksEnrouteToWell
                                                                        : 0}
                                                                </span>
                                                            </Row>
                                                        </Col>
                                                    </div>
                                                </InfoWindow>
                                            ) : null}

                                            {selectDriverPath && (
                                                <Polyline
                                                    path={selectDriverPath}
                                                    options={{
                                                        strokeColor: '#FF0000', //'#426289',
                                                        strokeOpacity: 0.8,
                                                        strokeWeight: 3,
                                                        icons: [
                                                            {
                                                                icon: lineSymbol,
                                                                // offset: '25px',
                                                                repeat: '100px',
                                                            },
                                                        ],
                                                    }}
                                                />
                                            )}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};
export default DashboardMap;
